import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/layouts/AdminLayout';
import SearchDropdown from '../../../components/SearchDropdown';
import ProductBulkUploadModal from '../../../components/ProductBulkUploadModal';
import { getProducts, deleteProduct, bulkDeleteProducts } from '../../../services/productService';
import { getCategories } from '../../../services/categoryService';
import { getSubcategories } from '../../../services/subcategoryService';
import { getTags } from '../../../services/tagService';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit, FiTrash2, FiEye, FiFilter, FiSearch, FiX, FiUpload, FiDownload } from 'react-icons/fi';
import { exportToCSV, productColumns } from '../../../utils/csvExport';

const ProductsPage = () => {
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [tags, setTags] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [productToDelete, setProductToDelete] = useState(null);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0
    });
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
    const [selectAll, setSelectAll] = useState(false);
    const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
    const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);

    const [filters, setFilters] = useState({
        search: '',
        category: '',
        subcategory: '',
        tags: '',
        minPrice: '',
        maxPrice: '',
        minRating: ''
    });

    useEffect(() => {
        fetchCategories();
        fetchProducts();
    }, []);

    useEffect(() => {
        if (filters.category) {
            fetchSubcategories(filters.category);
        } else {
            setSubcategories([]);
            setFilters(prev => ({ ...prev, subcategory: '', tags: '' }));
        }
    }, [filters.category]);

    useEffect(() => {
        if (filters.subcategory) {
            fetchTags(filters.subcategory);
        } else {
            setTags([]);
            setFilters(prev => ({ ...prev, tags: '' }));
        }
    }, [filters.subcategory]);

    const fetchCategories = async () => {
        try {
            const response = await getCategories();
            setCategories(response.data || []);
        } catch (error) {
            console.error('Error fetching categories:', error);
            toast.error('Failed to load categories');
        }
    };

    const fetchSubcategories = async (categoryId) => {
        try {
            const response = await getSubcategories(categoryId);
            setSubcategories(response.data || []);
        } catch (error) {
            console.error('Error fetching subcategories:', error);
            toast.error('Failed to load subcategories');
        }
    };

    const fetchTags = async (subcategoryId) => {
        try {
            const response = await getTags({ subcategoryId });
            setTags(response.data || []);
        } catch (error) {
            console.error('Error fetching tags:', error);
            toast.error('Failed to load tags');
        }
    };

    const fetchProducts = async (page = 1) => {
        try {
            setLoading(true);

            // Build query parameters
            const params = {
                page,
                limit: pagination.limit
            };

            // Add filters if they have values
            if (filters.search) params.search = filters.search;
            if (filters.category) params.category = filters.category;
            if (filters.subcategory) params.subcategory = filters.subcategory;
            if (filters.tags) params.tags = filters.tags;

            // Add price filters in MongoDB query format
            if (filters.minPrice) params['price[gte]'] = filters.minPrice;
            if (filters.maxPrice) params['price[lte]'] = filters.maxPrice;

            // Add rating filter in MongoDB query format
            if (filters.minRating) params['rating[gte]'] = filters.minRating;

            const response = await getProducts(params);

            setProducts(response.data || []);
            setPagination({
                page,
                limit: pagination.limit,
                total: response.total || 0,
                totalPages: Math.ceil((response.total || 0) / pagination.limit)
            });

            // Reset selections when fetching new data
            setSelectedProducts([]);
            setSelectAll(false);
        } catch (error) {
            console.error('Error fetching products:', error);
            toast.error('Failed to load products');
        } finally {
            setLoading(false);
        }
    };

    const handleCheckboxChange = (productId) => {
        setSelectedProducts(prev => {
            if (prev.includes(productId)) {
                return prev.filter(id => id !== productId);
            } else {
                return [...prev, productId];
            }
        });
    };

    const handleSelectAllChange = () => {
        if (selectAll) {
            setSelectedProducts([]);
        } else {
            setSelectedProducts(products.map(product => product._id));
        }
        setSelectAll(!selectAll);
    };

    const handleDeleteClick = (product) => {
        setProductToDelete(product);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (!productToDelete) return;

        try {
            await deleteProduct(productToDelete._id);
            toast.success(`Product "${productToDelete.name}" deleted successfully`);
            fetchProducts(pagination.page);
        } catch (error) {
            console.error('Error deleting product:', error);
            toast.error(error.response?.data?.message || 'Failed to delete product');
        } finally {
            setShowDeleteModal(false);
            setProductToDelete(null);
        }
    };

    const handleBulkDeleteClick = () => {
        if (selectedProducts.length === 0) {
            toast.info('No products selected');
            return;
        }
        setShowBulkDeleteModal(true);
    };

    const handleBulkDeleteConfirm = async () => {
        if (selectedProducts.length === 0) return;

        try {
            // Use the bulkDeleteProducts function instead of individual deleteProduct calls
            const result = await bulkDeleteProducts(selectedProducts);

            // Show appropriate messages based on the response
            if (result.notFound && result.notFound.length > 0) {
                toast.warning(`${result.notFound.length} products were not found`);
            }

            if (result.notAuthorized && result.notAuthorized.length > 0) {
                toast.warning(`You are not authorized to delete ${result.notAuthorized.length} products`);
            }

            if (result.deletedCount > 0) {
                toast.success(`${result.deletedCount} products deleted successfully`);
            } else {
                toast.error('No products were deleted');
            }

            // Refresh the list
            fetchProducts(pagination.page);
        } catch (error) {
            console.error('Error in bulk delete operation:', error);
            toast.error(error.response?.data?.message || 'Failed to complete bulk delete operation');
        } finally {
            setShowBulkDeleteModal(false);
            setSelectedProducts([]);
            setSelectAll(false);
        }
    };

    const handleDeleteAllClick = () => {
        if (pagination.total === 0) {
            toast.info('No products to delete');
            return;
        }
        setShowDeleteAllModal(true);
    };

    const handleDeleteAllConfirm = async () => {
        try {
            // Fetch all product IDs without pagination
            const response = await getProducts({ limit: 10000 }); // Get a large number to ensure we get all
            const allProducts = response.data || [];
            
            if (allProducts.length === 0) {
                toast.info('No products found to delete');
                setShowDeleteAllModal(false);
                return;
            }

            const allProductIds = allProducts.map(product => product._id);

            // Use the bulkDeleteProducts function
            const result = await bulkDeleteProducts(allProductIds);

            // Show appropriate messages based on the response
            if (result.notFound && result.notFound.length > 0) {
                toast.warning(`${result.notFound.length} products were not found`);
            }

            if (result.notAuthorized && result.notAuthorized.length > 0) {
                toast.warning(`You are not authorized to delete ${result.notAuthorized.length} products`);
            }

            if (result.deletedCount > 0) {
                toast.success(`${result.deletedCount} products deleted successfully`);
            } else {
                toast.error('No products were deleted');
            }

            // Refresh the list
            fetchProducts(1);
        } catch (error) {
            console.error('Error in delete all operation:', error);
            toast.error(error.response?.data?.message || 'Failed to delete all products');
        } finally {
            setShowDeleteAllModal(false);
            setSelectedProducts([]);
            setSelectAll(false);
        }
    };

    const handlePageChange = (page) => {
        fetchProducts(page);
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleSearchChange = (searchValue) => {
        setFilters(prev => ({ ...prev, search: searchValue }));
    };

    const handleProductSelect = (product) => {
        // When a product is selected from dropdown, navigate to its edit page
        navigate(`/admin/products/${product._id}/edit`);
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        fetchProducts(1);
    };

    // Real-time search effect
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            fetchProducts(1);
        }, 500); // Debounce search by 500ms

        return () => clearTimeout(timeoutId);
    }, [filters.search]);

    const clearFilters = () => {
        setFilters({
            search: '',
            category: '',
            subcategory: '',
            tags: '',
            minPrice: '',
            maxPrice: '',
            minRating: ''
        });
        fetchProducts(1);
    };

    const handleExportCSV = () => {
        if (products.length === 0) {
            toast.info('No products to export');
            return;
        }

        try {
            const timestamp = new Date().toISOString().split('T')[0];
            const filename = `products_export_${timestamp}.csv`;
            exportToCSV(products, productColumns, filename);
            toast.success('Products exported successfully');
        } catch (error) {
            console.error('Error exporting products:', error);
            toast.error('Failed to export products');
        }
    };

    return (
        <AdminLayout>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-800">Products</h1>
                <div className="flex space-x-2">
                    {selectedProducts.length > 0 && (
                        <button
                            onClick={handleBulkDeleteClick}
                            className="btn btn-outline border-red-300 text-red-600 hover:bg-red-50 flex items-center"
                        >
                            <FiTrash2 className="mr-2" /> Delete Selected ({selectedProducts.length})
                        </button>
                    )}
                    <button
                        onClick={handleDeleteAllClick}
                        className="btn btn-outline border-red-500 text-red-700 hover:bg-red-100 flex items-center"
                        title="Delete all products in the database"
                    >
                        <FiTrash2 className="mr-2" /> Delete All
                    </button>
                    <button
                        onClick={handleExportCSV}
                        className="btn btn-outline border-green-300 text-green-600 hover:bg-green-50 flex items-center"
                    >
                        <FiDownload className="mr-2" /> Export CSV
                    </button>
                    <button
                        onClick={() => setShowBulkUploadModal(true)}
                        className="btn btn-outline border-blue-300 text-blue-600 hover:bg-blue-50 flex items-center"
                    >
                        <FiUpload className="mr-2" /> Bulk Upload
                    </button>
                    <Link to="/admin/products/new" className="btn btn-primary flex items-center">
                        <FiPlus className="mr-2" /> Add Product
                    </Link>
                </div>
            </div>

            <div className="bg-[#f8f8f8] shadow-md rounded-lg p-4 mb-6">
                <form onSubmit={handleSearchSubmit}>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div className="md:col-span-3">
                            <SearchDropdown
                                value={filters.search}
                                onChange={handleSearchChange}
                                onSelect={handleProductSelect}
                                placeholder="Search products by name, brand, or description..."
                                className="w-full"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Category
                            </label>
                            <select
                                name="category"
                                value={filters.category}
                                onChange={handleFilterChange}
                                className="input"
                            >
                                <option value="">All Categories</option>
                                {categories.map(category => (
                                    <option key={category._id} value={category._id}>
                                        {category.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Subcategory
                            </label>
                            <select
                                name="subcategory"
                                value={filters.subcategory}
                                onChange={handleFilterChange}
                                className="input"
                                disabled={!filters.category}
                            >
                                <option value="">All Subcategories</option>
                                {subcategories.map(subcategory => (
                                    <option key={subcategory._id} value={subcategory._id}>
                                        {subcategory.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Tags
                            </label>
                            <select
                                name="tags"
                                value={filters.tags}
                                onChange={handleFilterChange}
                                className="input"
                                disabled={!filters.subcategory}
                            >
                                <option value="">All Tags</option>
                                {tags.map(tag => (
                                    <option key={tag._id} value={tag._id}>
                                        {tag.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Min Rating
                            </label>
                            <select
                                name="minRating"
                                value={filters.minRating}
                                onChange={handleFilterChange}
                                className="input"
                            >
                                <option value="">Any Rating</option>
                                <option value="5">5 Stars</option>
                                <option value="4">4+ Stars</option>
                                <option value="3">3+ Stars</option>
                                <option value="2">2+ Stars</option>
                                <option value="1">1+ Star</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Min Price
                            </label>
                            <input
                                type="number"
                                name="minPrice"
                                value={filters.minPrice}
                                onChange={handleFilterChange}
                                placeholder="Min"
                                min="0"
                                className="input"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Max Price
                            </label>
                            <input
                                type="number"
                                name="maxPrice"
                                value={filters.maxPrice}
                                onChange={handleFilterChange}
                                placeholder="Max"
                                min="0"
                                className="input"
                            />
                        </div>

                        <div className="flex items-end space-x-2">
                            <button
                                type="submit"
                                className="btn btn-primary py-2"
                            >
                                <FiSearch className="mr-2" /> Search
                            </button>
                            <button
                                type="button"
                                onClick={clearFilters}
                                className="btn btn-outline py-2"
                            >
                                <FiX className="mr-2" /> Clear
                            </button>
                        </div>
                    </div>
                </form>
            </div>

            {loading ? (
                <div className="flex justify-center py-10">
                    <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-600"></div>
                </div>
            ) : (
                <>
                    <div className="bg-[#f8f8f8] shadow-md rounded-lg overflow-hidden">
                        {products.length === 0 ? (
                            <div className="p-6 text-center text-gray-500">
                                No products found. Try different filters or click the "Add Product" button to create one.
                            </div>
                        ) : (
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-3 py-3 text-left">
                                            <div className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                                                    checked={selectAll}
                                                    onChange={handleSelectAllChange}
                                                />
                                            </div>
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Product
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Category
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Tags
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Price
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Rating
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-[#f8f8f8] divide-y divide-gray-200">
                                    {products.map((product) => (
                                        <tr key={product._id} className={`hover:bg-gray-50 ${selectedProducts.includes(product._id) ? 'bg-blue-50' : ''}`}>
                                            <td className="px-3 py-4">
                                                <div className="flex items-center">
                                                    <input
                                                        type="checkbox"
                                                        className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                                                        checked={selectedProducts.includes(product._id)}
                                                        onChange={() => handleCheckboxChange(product._id)}
                                                    />
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <div className="flex-shrink-0 h-10 w-10 mr-3">
                                                        <img
                                                            className="h-10 w-10 rounded-md object-cover"
                                                            src={product.imageUrl || '/placeholder.png'}
                                                            alt={product.name}
                                                        />
                                                    </div>
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-900">{product.name}</div>
                                                        <div className="text-sm text-gray-500">{product.brand}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-gray-900">
                                                    {product.category?.name || 'N/A'}
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                    {product.subcategory?.name || 'N/A'}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-gray-900">
                                                    {product.tags && product.tags.length > 0 ? (
                                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                            {product.tags.length} {product.tags.length === 1 ? 'tag' : 'tags'}
                                                        </span>
                                                    ) : (
                                                        <span className="text-xs text-red-500">No tags</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900">
                                                    ${product.price?.toFixed(2) || '0.00'}
                                                </div>
                                                {product.discount > 0 && (
                                                    <div className="text-xs text-green-600">
                                                        {product.discount}% off
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <span className="text-sm text-gray-900 mr-1">{product.rating || 0}</span>
                                                    <span className="text-xs text-primary-600">★</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${product.isActive
                                                    ? 'bg-green-100 text-green-800'
                                                    : 'bg-red-100 text-red-800'
                                                    }`}>
                                                    {product.isActive ? 'Active' : 'Inactive'}
                                                </span>
                                                {product.isFeatured && (
                                                    <span className="ml-1 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                                                        Featured
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                <div className="flex space-x-3">
                                                    <Link
                                                        to={`/admin/products/${product._id}/view`}
                                                        className="text-gray-600 hover:text-primary-600"
                                                    >
                                                        <FiEye size={18} />
                                                    </Link>
                                                    <Link
                                                        to={`/admin/products/${product._id}/edit`}
                                                        className="text-gray-600 hover:text-primary-600"
                                                    >
                                                        <FiEdit size={18} />
                                                    </Link>
                                                    <button
                                                        onClick={() => handleDeleteClick(product)}
                                                        className="text-gray-600 hover:text-primary-600"
                                                    >
                                                        <FiTrash2 size={18} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {/* Pagination */}
                    {pagination.totalPages > 1 && (
                        <div className="flex justify-center mt-6">
                            <nav className="inline-flex rounded-md shadow">
                                <button
                                    onClick={() => handlePageChange(1)}
                                    disabled={pagination.page === 1}
                                    className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-[#f8f8f8] text-sm font-medium ${pagination.page === 1
                                        ? 'text-gray-300 cursor-not-allowed'
                                        : 'text-gray-500 hover:bg-gray-50'
                                        }`}
                                >
                                    First
                                </button>
                                <button
                                    onClick={() => handlePageChange(pagination.page - 1)}
                                    disabled={pagination.page === 1}
                                    className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-[#f8f8f8] text-sm font-medium ${pagination.page === 1
                                        ? 'text-gray-300 cursor-not-allowed'
                                        : 'text-gray-500 hover:bg-gray-50'
                                        }`}
                                >
                                    Prev
                                </button>
                                <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-[#f8f8f8] text-sm font-medium text-gray-700">
                                    Page {pagination.page} of {pagination.totalPages}
                                </span>
                                <button
                                    onClick={() => handlePageChange(pagination.page + 1)}
                                    disabled={pagination.page === pagination.totalPages}
                                    className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-[#f8f8f8] text-sm font-medium ${pagination.page === pagination.totalPages
                                        ? 'text-gray-300 cursor-not-allowed'
                                        : 'text-gray-500 hover:bg-gray-50'
                                        }`}
                                >
                                    Next
                                </button>
                                <button
                                    onClick={() => handlePageChange(pagination.totalPages)}
                                    disabled={pagination.page === pagination.totalPages}
                                    className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-[#f8f8f8] text-sm font-medium ${pagination.page === pagination.totalPages
                                        ? 'text-gray-300 cursor-not-allowed'
                                        : 'text-gray-500 hover:bg-gray-50'
                                        }`}
                                >
                                    Last
                                </button>
                            </nav>
                        </div>
                    )}
                </>
            )}

            {/* Single Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                            <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                        </div>

                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                        <div className="inline-block align-bottom bg-[#f8f8f8] rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                            <div className="bg-[#f8f8f8] px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                <div className="sm:flex sm:items-start">
                                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                        <FiTrash2 className="h-6 w-6 text-red-600" aria-hidden="true" />
                                    </div>
                                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                        <h3 className="text-lg leading-6 font-medium text-gray-900">Delete Product</h3>
                                        <div className="mt-2">
                                            <p className="text-sm text-gray-500">
                                                Are you sure you want to delete the product "{productToDelete?.name}"? This action cannot be undone.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                <button
                                    type="button"
                                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                                    onClick={handleDeleteConfirm}
                                >
                                    Delete
                                </button>
                                <button
                                    type="button"
                                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-[#f8f8f8] text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                    onClick={() => setShowDeleteModal(false)}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Bulk Delete Confirmation Modal */}
            {showBulkDeleteModal && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                            <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                        </div>

                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                        <div className="inline-block align-bottom bg-[#f8f8f8] rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                            <div className="bg-[#f8f8f8] px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                <div className="sm:flex sm:items-start">
                                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                        <FiTrash2 className="h-6 w-6 text-red-600" aria-hidden="true" />
                                    </div>
                                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                        <h3 className="text-lg leading-6 font-medium text-gray-900">Delete Selected Products</h3>
                                        <div className="mt-2">
                                            <p className="text-sm text-gray-500">
                                                Are you sure you want to delete {selectedProducts.length} selected products? This action cannot be undone.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                <button
                                    type="button"
                                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                                    onClick={handleBulkDeleteConfirm}
                                >
                                    Delete All Selected
                                </button>
                                <button
                                    type="button"
                                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-[#f8f8f8] text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                    onClick={() => setShowBulkDeleteModal(false)}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete All Products Confirmation Modal */}
            {showDeleteAllModal && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                            <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                        </div>

                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                        <div className="inline-block align-bottom bg-[#f8f8f8] rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                            <div className="bg-[#f8f8f8] px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                <div className="sm:flex sm:items-start">
                                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                        <FiTrash2 className="h-6 w-6 text-red-600" aria-hidden="true" />
                                    </div>
                                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                        <h3 className="text-lg leading-6 font-medium text-gray-900">Delete All Products</h3>
                                        <div className="mt-2">
                                            <p className="text-sm text-gray-500">
                                                Are you sure you want to delete <strong>ALL {pagination.total} products</strong> from the database? This action cannot be undone and will permanently remove all product data.
                                            </p>
                                            <p className="text-sm text-red-600 font-semibold mt-3">
                                                ⚠️ WARNING: This will delete every product in your database!
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                <button
                                    type="button"
                                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                                    onClick={handleDeleteAllConfirm}
                                >
                                    Yes, Delete All Products
                                </button>
                                <button
                                    type="button"
                                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-[#f8f8f8] text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                    onClick={() => setShowDeleteAllModal(false)}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Bulk Upload Modal */}
            <ProductBulkUploadModal
                isOpen={showBulkUploadModal}
                onClose={() => setShowBulkUploadModal(false)}
                onSuccess={() => {
                    fetchProducts(pagination.page);
                    setShowBulkUploadModal(false);
                }}
            />
        </AdminLayout>
    );
};

export default ProductsPage;